import Link from 'next/link'
import styles from './AppMenu.module.scss'
import { useTranslation } from 'next-i18next'
import { FC, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars } from '@fortawesome/free-solid-svg-icons'
import { useSession } from 'next-auth/react'
import { ConnectWallet } from '@thirdweb-dev/react'
import { MobileMenu } from '~/components/AppMenu/MobileMenu'
import { useRouter } from 'next/router'
import { DesktopMenu } from '~/components/AppMenu/DesktopMenu'
import { signIn } from '~/modules/Auth/Infrastructure/SignIn'
import { UiAppearance } from '~/modules/UiVariant/appearance'

interface Props {
  appearance?: UiAppearance
}

export const AppMenu: FC<Props> = ({ appearance = 'default' }) => {
  const { t } = useTranslation('app_menu')
  const session = useSession()
  const [openDesktopMenu, setOpenDesktopMenu] = useState(false)
  const [openMobileMenu, setOpenMobileMenu] = useState(false)
  const router = useRouter()
  let myTrophiesLinkHref = '/my-collection'
  let userAvatar = null
  let loginButton = null

  const walletButton = appearance === 'museumHome'
    ? null
    : (
        <div className={ styles.appMenu__connectWalletButton }>
          <ConnectWallet btnTitle={ t('connect_wallet_button') } />
        </div>
      )

  if (
    session.status === 'unauthenticated' &&
    router.pathname !== '/auth/signin'
  ) {
    myTrophiesLinkHref = '/auth/signin?callbackUrl=/my-collection'

    loginButton = (
      <button
        className={ styles.appMenu__loginButton }
        onClick={ () => signIn(router) }
      >
        { t('login_button_label') }
      </button>
    )
  }

  if (session.status === 'authenticated') {
    const user = session.data.user

    userAvatar = (
      <button
        type="button"
        className={ styles.appMenu__userAvatarButton }
        onClick={ () => {
          setOpenMobileMenu(false)
          setOpenDesktopMenu(!openDesktopMenu)
        } }
        aria-label={ t('open_user_menu_button') }
        aria-haspopup="menu"
        aria-expanded={ openDesktopMenu }
        aria-controls="app-menu-desktop-dropdown"
      >
        <object
          data={ user.image ?? undefined }
          className={ styles.appMenu__userAvatar }
        >
          <img
            className={ styles.appMenu__userAvatarDefault }
            src={ '/img/defaults/user.png' }
            alt={ t('user_avatar_alt', { name: user.name }) }
            width={ 56 }
            height={ 56 }
          />
        </object>
      </button>
    )
  }

  return (
    <nav
      className={ `
        ${styles.appMenu__navbar}
        ${appearance === 'museumHome' ? styles.museumHome : ''}
      ` }
    >
      <div className={ styles.appMenu__container }>
        <Link href="/" className={ styles.appMenu__logoImage } passHref>
          <img
            src="/brand-logo.svg"
            alt="Legend Museum"
          />
        </Link>

        <div className={ styles.appMenu__centerActions }>
          <Link
            href="/#featured-organizers"
            className={ `
              ${styles.appMenu__centerActionsLink}
            ` }
          >
            { t('organizers_section') }
          </Link>

          <Link
            href={ myTrophiesLinkHref }
            className={ `
              ${styles.appMenu__centerActionsLink}
              ${router.pathname === '/my-collection' ? styles.appMenu__centerActionsLink_active : ''}
            ` }
          >
            { t('my_collection_section') }
          </Link>
        </div>

        <div className={ styles.appMenu__rightActions }>
          { walletButton }

          { userAvatar }

          <DesktopMenu
            openMenu={ openDesktopMenu }
            setOpenMenu={ setOpenDesktopMenu }
          />

          { loginButton }

          <button
            type="button"
            title={ t('open_menu_button') }
            aria-label={ t('open_menu_button') }
            className={ styles.appMenu__openMobileMenuButton }
            onClick={ () => {
              setOpenDesktopMenu(false)
              setOpenMobileMenu(true)
            } }
          >
            <FontAwesomeIcon
              icon={ faBars }
            />
          </button>

          <MobileMenu
            openMenu={ openMobileMenu }
            setOpenMenu={ setOpenMobileMenu }
            appearance={ appearance }
          />
        </div>
      </div>
    </nav>
  )
}
